import { t as uiText } from '../i18n';
import React, { useEffect, useState } from 'react';

export type AxisState = 'on' | 'off' | 'mixed';

export interface PublishAxesSelection {
  rag: AxisState;
  web: AxisState;
}

interface PublishAxesMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択が 0 件のとき */
  disabled: boolean;
  /** いずれかの反映処理が走っている */
  busy: boolean;
  selectedCount: number;
  ragState: AxisState;
  webState: AxisState;
  /** ワークスペースの「ページをWeb公開する」。null は読み込み中 */
  htmlPagesEnabled: boolean | null;
  /** 公開 RAG が使えるか。NCN では false で、公開設定ボタン自体を押せなくする */
  canUsePublishSettings: boolean;
  /** publicRag.webPages が使えるか。false のとき「Webページとして公開する」は出さない */
  canPublishWebPages: boolean;
  onApply: (next: PublishAxesSelection) => void;
}

const stateLabel = (state: AxisState): string =>
  state === 'on' ? 'ON' : state === 'off' ? 'OFF' : uiText("Partially on");

/**
 * 選択したノートの公開設定を 2 軸で切り替えるメニュー。
 *
 * - 知識軸「AIの回答に使う」: 公開AIが内容を参照して答える。ページは公開しない
 * - ページ軸「Webページとして公開する」: notreu のURLで誰でも読めるページを作る
 *
 * NCN（publicRag が OFF）では公開設定ボタン自体を押せない。
 * publicRag.webPages が OFF（NCG free / starter）ではページ軸を出さない。
 *
 * 各トグルは下書きを変えるだけで、実際の反映は「確定」を押したとき。
 * ページ軸 ON は知識軸 ON を含意するため、下書きの時点で矛盾しないように寄せる。
 */
const PublishAxesMenu: React.FC<PublishAxesMenuProps> = ({
  open,
  onOpenChange,
  disabled,
  busy,
  selectedCount,
  ragState,
  webState,
  htmlPagesEnabled,
  canUsePublishSettings,
  canPublishWebPages,
  onApply,
}) => {
  const interactionBlocked = disabled || busy || !canUsePublishSettings;
  const showWebAxis = canPublishWebPages;
  const webBlockedByWorkspace = showWebAxis && htmlPagesEnabled !== true;

  const [draftRag, setDraftRag] = useState<AxisState>(ragState);
  const [draftWeb, setDraftWeb] = useState<AxisState>(webState);

  // 開いたとき・選択が変わったときは現在値に戻す
  useEffect(() => {
    setDraftRag(ragState);
    setDraftWeb(webState);
  }, [open, ragState, webState]);

  useEffect(() => {
    if (!canUsePublishSettings && open) onOpenChange(false);
  }, [canUsePublishSettings, open, onOpenChange]);

  const changeRag = (enabled: boolean) => {
    setDraftRag(enabled ? 'on' : 'off');
    // 知識軸を落とすとWebページも出せない
    if (!enabled) setDraftWeb('off');
  };

  const changeWeb = (enabled: boolean) => {
    setDraftWeb(enabled ? 'on' : 'off');
    // Webページを出すなら回答にも使う
    if (enabled) setDraftRag('on');
  };

  const dirty = draftRag !== ragState || (showWebAxis && draftWeb !== webState);

  const renderAxis = (options: {
    title: string;
    description: string;
    draft: AxisState;
    onChange: (enabled: boolean) => void;
    blocked?: boolean;
    blockedHint?: string;
  }) => {
    const axisDisabled = busy || options.blocked === true;
    return (
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#333' }}>{options.title}</div>
          <div style={{
            fontSize: '12px',
            fontWeight: 500,
            color: options.draft === 'off' ? '#6c757d' : 'var(--workspace-primary, #cfae91)',
            flexShrink: 0,
          }}>
            {stateLabel(options.draft)}
          </div>
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', lineHeight: 1.5 }}>
          {options.description}
        </div>
        {options.blocked && options.blockedHint && (
          <div style={{ fontSize: '11px', color: '#b45309', marginTop: '6px', lineHeight: 1.5 }}>
            {options.blockedHint}
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
          {([true, false] as const).map((value) => {
            const active = options.draft === (value ? 'on' : 'off');
            return (
              <button
                key={String(value)}
                type="button"
                onClick={() => { if (!axisDisabled) options.onChange(value); }}
                disabled={axisDisabled}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '12px',
                  fontWeight: 500,
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: active ? 'var(--workspace-primary, #cfae91)' : '#d0d0d0',
                  backgroundColor: active ? 'var(--workspace-primary, #cfae91)' : '#fff',
                  color: active ? '#fff' : '#495057',
                  cursor: axisDisabled ? 'not-allowed' : 'pointer',
                  opacity: axisDisabled ? 0.6 : 1,
                }}
              >
                {value ? 'ON' : 'OFF'}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', marginLeft: '8px', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => { if (!interactionBlocked) onOpenChange(!open); }}
        disabled={interactionBlocked}
        aria-expanded={open}
        title={!canUsePublishSettings ? uiText("Publish settings are unavailable on this plan.") : undefined}
        style={{
          padding: '6px 12px',
          backgroundColor: interactionBlocked ? '#ccc' : 'var(--workspace-primary, #cfae91)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: interactionBlocked ? 'not-allowed' : 'pointer',
          fontSize: '12px',
        }}
      >
        {busy ? uiText("Applying...") : uiText("Publish settings")}
      </button>

      {open && (
        <>
          <div
            onClick={() => onOpenChange(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              width: '320px',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
              zIndex: 999,
              textAlign: 'left',
            }}
          >
            <div style={{
              padding: '10px 14px',
              borderBottom: '1px solid #f0f0f0',
              fontSize: '12px',
              color: '#666',
            }}>
              {uiText("Currently selected")} {selectedCount} {uiText("items will be applied")}
            </div>

            {renderAxis({
              title: uiText("Use in AI answers"),
              description: uiText("Public AI answers using this note. The note itself is not published."),
              draft: draftRag,
              onChange: changeRag,
            })}

            {showWebAxis && (
              <>
                <div style={{ borderTop: '1px solid #f0f0f0' }} />

                {renderAxis({
                  title: uiText("Publish as a web page"),
                  description: uiText("Creates a page anyone can read at a notreu URL. When turned on, it is also used in AI answers."),
                  draft: draftWeb,
                  onChange: changeWeb,
                  blocked: webBlockedByWorkspace,
                  blockedHint: htmlPagesEnabled === null
                    ? uiText("Loading settings...")
                    : uiText("Unavailable because \"Publish page on the web\" is off in publish settings."),
                })}
              </>
            )}

            <div style={{
              display: 'flex',
              gap: '8px',
              padding: '12px 14px',
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fafafa',
              borderRadius: '0 0 8px 8px',
            }}>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                style={{
                  flex: 1,
                  padding: '8px',
                  fontSize: '12px',
                  fontWeight: 500,
                  borderRadius: '4px',
                  border: '1px solid #d0d0d0',
                  backgroundColor: '#fff',
                  color: '#495057',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {uiText("Cancel")}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (dirty && !busy) {
                    onApply({ rag: draftRag, web: showWebAxis ? draftWeb : webState });
                  }
                }}
                disabled={!dirty || busy}
                style={{
                  flex: 1,
                  padding: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: !dirty || busy ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                  color: '#fff',
                  cursor: !dirty || busy ? 'not-allowed' : 'pointer',
                }}
              >
                {uiText("Confirm")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PublishAxesMenu;
